/**
 * Maps camelized prefill GET response contact to the BasicDetailsStep state shape.
 * Handles both camelized (from api.camelizeKeys) and snake_case keys as a fallback.
 */
export function getPrefillContactState(prefillData) {
  const c = prefillData?.contact || {};
  return {
    email: c.email || '',
    fullName: c.fullName || c.full_name || '',
    companyName: c.companyName || c.company_name || '',
    phone: c.phone || '',
    country: c.country || '',
  };
}

/**
 * Determines whether/how to show the Account Details step for a prefill onboarding.
 *
 * Returns:
 *   'none'     — admin filled all fields; skip the step entirely, go straight to agreement
 *   'skip'     — required fields present but optional (phone/country) missing;
 *                show the step with a "Skip" button so the customer can proceed without them
 *   'required' — at least one required field (email, fullName, companyName) is absent;
 *                show the step without a skip option
 */
export function getPrefillAccountStepMode(prefillData) {
  const c = prefillData?.contact || {};
  const email = (c.email || '').trim();
  const fullName = (c.fullName || c.full_name || '').trim();
  const companyName = (c.companyName || c.company_name || '').trim();
  const phone = (c.phone || '').trim();
  const country = (c.country || '').trim();

  const requiredPresent = !!(email && fullName && companyName);
  const optionalPresent = !!(phone && country);

  if (requiredPresent && optionalPresent) return 'none';
  if (requiredPresent) return 'skip';
  return 'required';
}

/**
 * Returns which contact fields were pre-filled by the admin (non-empty on arrival).
 * These should be shown as read-only to the customer.
 */
export function getPrefillLockedFields(prefillData) {
  const c = prefillData?.contact || {};
  return {
    email: !!(c.email || '').trim(),
    fullName: !!(c.fullName || c.full_name || '').trim(),
    companyName: !!(c.companyName || c.company_name || '').trim(),
    phone: !!(c.phone || '').trim(),
    country: !!(c.country || '').trim(),
  };
}

/**
 * Builds the submission payload for a token-driven prefill onboarding.
 * Sends the prefill token (from the URL), updated contact details,
 * and the customer's signed agreement.
 */
export function buildPrefillSubmissionPayload(prefillToken, contactState, signatureBase64) {
  /* eslint-disable camelcase */
  return {
    prefill_token: prefillToken,
    contact: {
      email: (contactState.email || '').trim(),
      full_name: contactState.fullName || undefined,
      company_name: contactState.companyName || undefined,
      phone: contactState.phone || undefined,
      country: contactState.country || undefined,
    },
    agreement: {
      accepted: true,
      signature: signatureBase64,
    },
  };
  /* eslint-enable camelcase */
}
